import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AiFillHome,
  AiFillQuestionCircle,
  AiOutlineLogin,
  AiOutlineClose,
  AiOutlineMenu,
} from "react-icons/ai";
import { HiSupport, HiInformationCircle } from "react-icons/hi";
import { FaTachometerAlt } from "react-icons/fa";

export const TopNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Get user authentication state
  const { user, token } = useSelector((state) => ({
    user: state.userData.user,
    token: state.userData.token,
  }));

  const isLoggedIn = !!(user && token);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  const navLinks = [
    { name: "Home", href: "/", Icon: AiFillHome },
    { name: "About", href: "/about", Icon: AiFillQuestionCircle },
    // { name: "Invest", href: "/plans", Icon: HiSparkles },
    { name: "Support", href: "/support", Icon: HiSupport },
    { name: "FAQ", href: "/faq", Icon: HiInformationCircle },
  ];

  return (
    <>
      {/* --- DESKTOP NAVIGATION --- */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 hidden lg:block ${
          scrolled
            ? "bg-white shadow-sm py-3 border-b border-gray-100"
            : "bg-white py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex-none">
            <img
              className="h-12 w-auto object-contain"
              src="/img/logo/logo.png"
              alt="Logo"
            />
          </Link>

          {/* Center Links */}
          <div className="flex items-center gap-8">
            {navLinks.map(({ name, href }, index) => {
              const isActive = location.pathname === href;
              return (
                <Link
                  key={index}
                  to={href}
                  className={`relative text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "text-primary-600"
                      : "text-gray-600 hover:text-primary-600"
                  }`}
                >
                  {name}
                  {/* Active Dot Indicator */}
                  {isActive && (
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-600 rounded-full"></span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-blue-500 text-white text-sm font-semibold shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:scale-105 transition-all duration-300"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-primary-600 transition-colors"
                >
                  <AiOutlineLogin className="text-lg" />
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-blue-500 text-white text-sm font-semibold shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:scale-105 transition-all duration-300"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* --- MOBILE NAVIGATION BAR --- */}
      <nav
        className={`fixed w-full z-50 lg:hidden transition-all duration-300 ${
          scrolled || isOpen ? "bg-white shadow-sm" : "bg-transparent bg-white"
        }`}
      >
        <div className="px-4 py-2 flex justify-between items-center">
          <Link to="/">
            <img className="h-9 w-auto" src="/img/logo/logo.png" alt="Logo" />
          </Link>

          <button
            onClick={() => setIsOpen(true)}
            className={`p-2   ${
              scrolled ? "text-gray-700" : "text-white"
            } hover:bg-gray-100 rounded-lg transition-colors`}
          >
            <AiOutlineMenu className="text-2xl" />
          </button>
        </div>
      </nav>

      {/* --- MOBILE FULL SCREEN MENU (Custom Drawer) --- */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Overlay Background */}
        <div className="absolute inset-0 bg-white">
          {/* Header */}
          <div className="px-4 py-4 flex justify-between items-center border-b border-gray-100">
            <img className="h-9" src="/img/logo/logo.png" alt="Logo" />
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
            >
              <AiOutlineClose className="text-2xl" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex flex-col p-6 gap-2">
            <div className="space-y-1">
              {navLinks.map(({ name, href, Icon }, index) => {
                const isActive = location.pathname === href;
                return (
                  <Link
                    key={index}
                    to={href}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                      isActive
                        ? "bg-primary-50 text-primary-600 font-bold"
                        : "text-gray-600 hover:bg-gray-50 font-medium"
                    }`}
                  >
                    <Icon className="text-xl" />
                    <span className="text-lg">{name}</span>
                  </Link>
                );
              })}
            </div>

            {/* Mobile Auth Buttons */}
            <div className="mt-8 space-y-4">
              {isLoggedIn ? (
                <Link
                  to="/dashboard"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-white font-bold shadow-lg hover:bg-primary-700 transition-colors"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
                  >
                    <AiOutlineLogin /> Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-center w-full py-3 rounded-xl bg-primary text-white font-bold shadow-lg hover:bg-primary-700 transition-colors"
                  >
                    Create Account
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
